import { Link, useParams } from "react-router";
import { FolderOpen } from "lucide-react";
import { AdminAlert, AdminPageHeader } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  brandSiteAgentScoreCopy,
  brandSiteStatusLabel,
  brandSiteTooFewPagesCopy,
  type BrandSite,
} from "@/lib/brand-site-api";
import {
  brandSiteNextStep,
  type BrandSiteStepAction,
} from "@/lib/brand-site-overview";
import { brandSitePath } from "@/lib/brand-site-routing";
import { useBrandSiteWorkbench } from "@/pages/brand-site/BrandSiteSiteLayout";

/** 单站概览：只讲当前这座官网 */
export default function BrandSiteOverviewPage() {
  const { brandId } = useParams();
  const { accountId } = useAccountWorkspace();
  const { currentSite } = useBrandSiteWorkbench();

  const step = brandSiteNextStep(currentSite);
  const scoreCopy = brandSiteAgentScoreCopy(currentSite);
  const tooFewPagesCopy = brandSiteTooFewPagesCopy(currentSite);

  function renderAction(site: BrandSite, action: BrandSiteStepAction, variant: "default" | "outline") {
    if (action.to === "external" && action.href) {
      return (
        <Button key={action.label} asChild variant={variant} size="sm">
          <a href={action.href} target="_blank" rel="noreferrer">
            {action.label}
          </a>
        </Button>
      );
    }

    const to =
      action.to === "edit"
        ? brandSitePath(accountId, brandId!, site.id, "edit")
        : action.to === "publish"
          ? brandSitePath(accountId, brandId!, site.id, "publish")
          : brandSitePath(accountId, brandId!, site.id, "domain");

    return (
      <Button key={action.label} asChild variant={variant} size="sm">
        <Link to={to}>{action.label}</Link>
      </Button>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title={currentSite.title}
        description="这一座官网。改内容、发布、绑自己的域名都在这里。"
      />

      {step ? (
        <>
          <div className="space-y-3 rounded-lg border border-border bg-card px-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-medium text-foreground">{step.title}</h2>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                {brandSiteStatusLabel(currentSite.status)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{step.body}</p>
            {tooFewPagesCopy ? <AdminAlert tone="info">{tooFewPagesCopy}</AdminAlert> : null}
            <div className="flex flex-wrap items-center gap-2">
              {renderAction(currentSite, step.primary, "default")}
              {step.secondary ? renderAction(currentSite, step.secondary, "outline") : null}
              <Button asChild variant="outline" size="sm">
                <Link
                  to={`/accounts/${accountId}/brands/${brandId}/sites/${currentSite.id}/materials`}
                >
                  <FolderOpen className="size-3.5" />
                  素材库
                </Link>
              </Button>
            </div>
          </div>

          <dl className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <dt className="text-sm text-muted-foreground">自己的域名</dt>
              <dd className="text-sm text-foreground">
                {currentSite.custom_host ? (
                  <a
                    href={`https://${currentSite.custom_host}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-primary-readable hover:underline"
                  >
                    {currentSite.custom_host}
                  </a>
                ) : (
                  <Link
                    to={brandSitePath(accountId, brandId!, currentSite.id, "domain")}
                    className="text-primary-readable hover:underline"
                  >
                    尚未绑定
                  </Link>
                )}
              </dd>
            </div>
            {currentSite.current_release ? (
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-sm text-muted-foreground">上次发布</dt>
                <dd className="text-sm text-foreground">
                  第 {currentSite.current_release.number} 次
                  {currentSite.current_release.published_at
                    ? ` · ${new Date(currentSite.current_release.published_at).toLocaleString("zh-CN")}`
                    : ""}
                </dd>
              </div>
            ) : null}
            {scoreCopy ? (
              <div className="flex items-start justify-between gap-4 px-4 py-3">
                <dt className="text-sm text-muted-foreground">智能体可读性</dt>
                <dd className="max-w-[18rem] text-right text-sm text-foreground">
                  <div>{scoreCopy.title.replace(/^智能体可读性\s+/, "")}</div>
                  <p className="mt-1 text-xs font-normal text-muted-foreground">{scoreCopy.hint}</p>
                </dd>
              </div>
            ) : null}
          </dl>
        </>
      ) : null}
    </div>
  );
}
